import { useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import AddTaskSideSheet from "@/features/global/components/AddTaskSideSheet";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import type { TaskAnalyticsSource } from "@/pages/tasks/_utils/amplitude";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";

type CreateGlobalTaskSideSheetProps = {
  sidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
  source?: TaskAnalyticsSource;
};

export default function CreateGlobalTaskSideSheet({
  sidebarOpen,
  setSidebarOpen,
  source
}: CreateGlobalTaskSideSheetProps) {
  const currentUser = useCurrentBackofficeUser();
  const { pathname } = useLocation();
  const personMatch = matchPath("/people/:pgaId/*", pathname);
  const lockedPgaIdRef = useRef<string | undefined>();
  const [resolvedPgaId, setResolvedPgaId] = useState<string | undefined>();

  useEffect(() => {
    if (!sidebarOpen) {
      lockedPgaIdRef.current = undefined;
      setResolvedPgaId(undefined);
      return;
    }

    if (!lockedPgaIdRef.current) {
      lockedPgaIdRef.current = personMatch?.params.pgaId;
    }

    setResolvedPgaId(lockedPgaIdRef.current);
  }, [sidebarOpen, personMatch?.params.pgaId]);

  const { data: personData } = useQuery(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId: resolvedPgaId || "" },
    skip: !sidebarOpen || !resolvedPgaId
  });

  const personId = personData?.person?.id;
  const defaultReferences = useMemo(
    () => (personId ? [{ referenceableId: personId, referenceableType: "PERSON" }] : undefined),
    [personId]
  );

  const analyticsSource: TaskAnalyticsSource = source ?? (resolvedPgaId ? "person_record" : "global_create");
  const personReferencePending = Boolean(resolvedPgaId) && !personId;

  const handleClose = () => {
    lockedPgaIdRef.current = undefined;
    setResolvedPgaId(undefined);
    setSidebarOpen(false);
  };

  return (
    <AddTaskSideSheet
      open={sidebarOpen}
      onClose={handleClose}
      currentUser={currentUser}
      source={analyticsSource}
      pgaId={resolvedPgaId}
      defaultReferences={defaultReferences}
      allowClearPersonReference={Boolean(resolvedPgaId)}
      personReferencePending={personReferencePending}
    />
  );
}
